/**
 * V114-5-1 — the gap rows on the system Monitoring tab (and the cross-system table's drill-in,
 * which is this same tab). One row per open gap from GET /gaps?system= (the four families, one
 * shape), each with its brief's ways forward — no dead ends. Panel round (v114-track5):
 *
 *   Fix with your agent   the row's secondary action (button-styled disclosure): the brief in
 *                         people's words (H3 — control titles + reasons in words; raw keys only in
 *                         the copyable command / markdown), options collapsed to chip + title with
 *                         the recommended one open, the pinned `gac fulfill` command with Copy, and
 *                         "Copy brief" (markdown).
 *   Review candidate ↓    a navigational jump to the existing candidate row (H4 — the real
 *                         "Accept and attest" stays there).
 *   Decline               named by its consequence (H5: "Scope out item…" / "Mark control N/A…" /
 *                         "Decline step…"); the quietest element. Monitoring-family declines sit
 *                         under a "More" overflow (B2); only a setup step keeps a direct button.
 *                         The dialog spells the consequence out BEFORE confirm (D-114-10) and is
 *                         described by it for screen readers (H1).
 *   PR open               a fix is already in review (D-114-8): the lead-in says so with the PR
 *                         link, "waiting for the change to be observed" and the forward-looking
 *                         "next check ~<date>" from the control's cadence.
 */
import { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Field, Spinner, StatusGlyph, Textarea } from '@aigovops/design-system';
import { useWorkspace } from '../../lib/workspace';
import {
  briefDisplayText, briefMarkdown, declineActionLabel, declineCapability, declineConsequence, declineReasonPlaceholder,
  gapReasonLabel, GAP_OPTION_KIND_LABELS,
  type FulfillmentBrief, type FulfillmentBriefOption, type FulfillmentGap,
} from '../../lib/gaps-api';
import { useDeclineGap, useGapBrief, useGaps, useIsProgramLead } from '../../hooks/useGaps';
import { monitoringErrorOf, type SystemMonitoringView } from '../../lib/monitoring-api';
import { controlAnchor, copyText, detectorLabel, findingHref } from '../../lib/monitoring-view';
import { fmtDate, plural } from '../../lib/format';
import { ActionDialog, JumpLink, useFocusAfter } from '../today/shared';
import { CopyField, ExternalOrApp, GatedButton, StatusChip } from './parts';

type View = SystemMonitoringView;

/** The row's subject in people's words (never an id when a name exists; never "CI"). */
function gapName(g: FulfillmentGap, title: (id: string) => string): string {
  if (g.family === 'enabler') return g.detail || (g.enabler_code ? g.enabler_code.replace(/[_:]+/g, ' ') : 'Setup step');
  if (g.family === 'release') return g.detail || 'Release gap';
  const ctl = g.control_id ? title(g.control_id) : 'Control';
  return g.component_name ? `${ctl} · ${g.component_name}` : ctl;
}

export function GapRows({ v, slug: _slug, prefix, title, onAnnounce }: {
  v: View; slug: string; prefix: string; title: (id: string) => string; onAnnounce: (m: string) => void;
}) {
  const q = useGaps(v.system.system_id);
  const { role } = useWorkspace();
  const programLead = useIsProgramLead();
  const [declining, setDeclining] = useState<FulfillmentGap | null>(null);
  // Low: an abandoned decline keeps its draft reason per gap (this mount).
  const drafts = useRef(new Map<string, string>());
  const focusAfter = useFocusAfter();
  const gaps = q.data?.gaps ?? [];
  if (!q.loading && !q.error && !gaps.length) return null;
  return (
    <Card density="compact" id="mon-gaps" tabIndex={-1} headingId="mon-gaps-h" title="Open gaps"
      right={gaps.length ? <p className="mon-card-note">{plural(gaps.length, 'gap')} · each closes on observed evidence</p> : undefined}>
      {q.loading ? <Spinner label="Loading gaps" /> : q.error ? (
        <Alert variant="error">Gaps could not be loaded. <Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>
      ) : (
        <ul className="mon-rows">
          {gaps.map((g) => (
            <GapRow key={g.gap_key} g={g} v={v} prefix={prefix} title={title}
              cap={declineCapability(g, { role, programLead })} onDecline={() => setDeclining(g)} />
          ))}
        </ul>
      )}
      <DeclineGapDialog gap={declining} title={title} drafts={drafts.current} onClose={() => setDeclining(null)}
        onDeclined={(g) => { setDeclining(null); drafts.current.delete(g.gap_key); onAnnounce(`Declined · ${gapName(g, title)}`); focusAfter('mon-gaps'); }} />
    </Card>
  );
}

export const gapAnchor = (gapKey: string) => `mon-gap-${gapKey.replace(/[^a-zA-Z0-9_-]/g, '_')}`;

function GapRow({ g, v, prefix, title, cap, onDecline }: {
  g: FulfillmentGap; v: View; prefix: string; title: (id: string) => string;
  cap: ReturnType<typeof declineCapability>; onDecline: () => void;
}) {
  const cand = g.family === 'monitoring' && g.control_id ? v.candidates.find((c) => !c.unplaced && c.control_id === g.control_id) ?? null : null;
  const declineLabel = declineActionLabel(g);
  // B2: monitoring-family declines live under "More"; only a setup step keeps a direct (quiet) button.
  const declineInMore = declineLabel !== null && g.family !== 'enabler';
  const cadence = g.control_id ? v.prescription.find((p) => p.control_id === g.control_id)?.cadence_seconds ?? null : null;
  const nextCheck = g.state === 'pr_open' && cadence ? new Date(Date.now() + cadence * 1000) : null;
  return (
    <li id={gapAnchor(g.gap_key)} tabIndex={-1} className="mon-row mon-row--block" data-part="gap-row">
      <span className="mon-row-main">
        <span className="mon-name">{gapName(g, title)} <StatusChip label={gapReasonLabel(g.reason)} tone={g.reason === 'last_check_not_passing' ? 'outline' : 'muted'} glyph={g.reason === 'last_check_not_passing' ? 'x' : 'circle'} /></span>
        {g.state === 'pr_open' && g.pr ? (
          <span className="mon-line" data-part="pr-open">
            <StatusChip label="PR open" tone="blue" glyph="dots" />
            <span>A fix is already in review —</span>
            <ExternalOrApp href={g.pr.pr_url}>Open PR</ExternalOrApp>
            <span className="mon-sub">reported {fmtDate(g.pr.reported_at)} · waiting for the change to be observed{nextCheck ? ` · next check ~${fmtDate(nextCheck.toISOString())}` : ''}</span>
          </span>
        ) : null}
        {g.finding_id ? <span className="mon-line"><Link to={findingHref(prefix, g.finding_id)}>Open finding</Link></span> : null}
      </span>
      <span className="mon-row-acts mon-gap-acts">
        <FixDisclosure gapKey={g.gap_key} gap={g} title={title} contextName={gapName(g, title)} />
        {cand ? <JumpLink className="gp-linkbtn gp-linkbtn--compact" to={`mon-cand-${cand.candidate_id}`}>Review candidate: {detectorLabel(cand.detector_kind, cand.detector_ref)} ↓</JumpLink>
          : g.family === 'monitoring' && g.control_id ? <JumpLink className="gp-linkbtn gp-linkbtn--compact" to={controlAnchor(g.control_id)}>See control ↓</JumpLink> : null}
        {g.family === 'release' ? (
          <span className="mon-sub">Closes by evidence only</span>
        ) : declineInMore ? (
          <details className="mon-more" data-part="gap-more">
            <summary>More<span className="gp-sr-only"> actions · {gapName(g, title)}</span></summary>
            <div className="mon-more-menu" role="presentation">
              <GatedButton cap={cap} ask={cap.whoCan} compact variant="ghost" onClick={onDecline}>{declineLabel}<span className="gp-sr-only"> · {gapName(g, title)}</span></GatedButton>
            </div>
          </details>
        ) : (
          <GatedButton cap={cap} ask={cap.whoCan} compact variant="ghost" onClick={onDecline}>{declineLabel}<span className="gp-sr-only"> · {gapName(g, title)}</span></GatedButton>
        )}
      </span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Fix with your agent — the brief, fetched when the disclosure opens
// ---------------------------------------------------------------------------

function FixDisclosure({ gapKey, gap, title, contextName }: { gapKey: string; gap: FulfillmentGap; title: (id: string) => string; contextName: string }) {
  const [open, setOpen] = useState(false);
  const brief = useGapBrief(gapKey, open);
  return (
    <details className="mon-disclose mon-gap-fix" data-part="fix-agent" onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary className="mon-gap-fix-summary">Fix with your agent<span className="gp-sr-only"> · {contextName}</span></summary>
      {!open ? null : brief.loading ? <Spinner label="Loading the brief" /> : brief.error || !brief.data ? (
        <Alert variant="error">The brief could not be loaded. <Button compact onClick={() => void brief.refetch()}>Retry</Button></Alert>
      ) : <BriefPanel brief={brief.data.brief} gap={gap} controlTitle={gap.control_id ? title(gap.control_id) : null} />}
    </details>
  );
}

function BriefOption({ o, open, gap, controlTitle }: { o: FulfillmentBriefOption; open: boolean; gap: FulfillmentGap; controlTitle: string | null }) {
  // Medium: options collapse to chip + title; the recommended (first-ranked) one starts open.
  return (
    <li data-part="brief-option">
      <details className="mon-disclose mon-brief-option" open={open || undefined}>
        <summary><StatusChip label={GAP_OPTION_KIND_LABELS[o.kind] ?? o.kind} tone="muted" /> <strong>{briefDisplayText(o.title, gap, controlTitle)}</strong></summary>
        <div className="mon-col">
          {o.kind === 'templated' && o.command ? <CopyField label="Run with your coding agent" value={o.command} /> : null}
          {o.steps.length ? (
            <ol className="mon-steps-list">
              {o.steps.map((s, n) => <li key={n}>{briefDisplayText(s, gap, controlTitle)}</li>)}
            </ol>
          ) : null}
        </div>
      </details>
    </li>
  );
}

function BriefPanel({ brief, gap, controlTitle }: { brief: FulfillmentBrief; gap: FulfillmentGap; controlTitle: string | null }) {
  const id = useId();
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle');
  const show = (text: string) => briefDisplayText(text, gap, controlTitle);
  return (
    <div className="mon-col" data-part="brief">
      <p className="mon-line mon-line--text"><strong>{show(brief.what)}</strong></p>
      <p className="mon-sub">{show(brief.why)}</p>
      {brief.options.length ? (
        <ol className="mon-rows mon-brief-options" aria-label="Ways to close this gap">
          {brief.options.map((o, i) => <BriefOption key={i} o={o} open={i === 0} gap={gap} controlTitle={controlTitle} />)}
        </ol>
      ) : null}
      <p className="mon-line" data-part="closes-when"><StatusGlyph kind="dots" size={12} /><span>Closes when: {show(brief.closing_observation.description)}</span></p>
      <div className="mon-actions">
        <Button compact aria-describedby={`${id}-copied`} onClick={async () => {
          setCopied(await copyText(briefMarkdown(brief)) ? 'copied' : 'failed');
        }}>{copied === 'copied' ? 'Brief copied' : 'Copy brief'}</Button>
        <p id={`${id}-copied`} role="status" aria-live="polite" className={copied === 'failed' ? 'mon-err' : 'gp-sr-only'}>
          {copied === 'failed' ? 'Copy didn’t work here. Open the brief with your keyboard and copy the text.' : copied === 'copied' ? 'Brief copied as markdown' : ''}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Decline — the consequence is spelled out BEFORE confirm (D-114-10; named by it, H5)
// ---------------------------------------------------------------------------

function DeclineGapDialog({ gap, title, drafts, onClose, onDeclined }: {
  gap: FulfillmentGap | null; title: (id: string) => string; drafts: Map<string, string>;
  onClose: () => void; onDeclined: (g: FulfillmentGap) => void;
}) {
  const uid = useId();
  const decline = useDeclineGap();
  const [reason, setReason] = useState('');
  const [forKey, setForKey] = useState<string | null>(null);
  const [reasonErr, setReasonErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const consequence = gap ? declineConsequence(gap) : null;
  // Restore the draft when a (new) gap opens the dialog.
  if (gap && forKey !== gap.gap_key) {
    setForKey(gap.gap_key);
    setReason(drafts.get(gap.gap_key) ?? '');
    setReasonErr(null);
    setError(null);
  }
  const close = () => {
    if (gap) drafts.set(gap.gap_key, reason);
    setForKey(null);
    onClose();
  };
  return (
    <ActionDialog open={!!gap} title={gap ? `${(declineActionLabel(gap) ?? 'Decline').replace(/…$/, '')}: ${gapName(gap, title)}` : ''}
      submitLabel={gap ? (declineActionLabel(gap) ?? 'Decline').replace(/…$/, '') : 'Decline'} danger
      busy={decline.isPending} error={error} subtitle={consequence}
      onClose={close}
      onSubmit={async () => {
        if (!gap) return false;
        if (!reason.trim()) { setReasonErr('Say why this gap does not apply.'); setTimeout(() => document.getElementById(`${uid}-r`)?.focus(), 0); return false; }
        setError(null);
        try {
          await decline.mutateAsync({ gapKey: gap.gap_key, reason: reason.trim() });
          setReason(''); setReasonErr(null); setForKey(null);
          onDeclined(gap);
          return true;
        } catch (e) {
          const f = monitoringErrorOf(e);
          setError(f.kind === 'not_found' ? 'This gap is not current any more; the list is refreshed.' : f.message || 'Could not decline.');
          return false;
        }
      }}>
      <Field id={`${uid}-r`} label="Reason" required error={reasonErr ?? undefined}>
        <Textarea rows={3} value={reason}
          placeholder={gap ? declineReasonPlaceholder(gap) : ''} onChange={(e) => { setReason(e.currentTarget.value); setReasonErr(null); }} />
      </Field>
      <p className="mon-hint">Recorded in the audit trail with your name and the date.</p>
    </ActionDialog>
  );
}
